import { DateTime } from '@mode-switch/express';
import { TempActions } from '@mode/charts/domain';
import { Resource } from '@mode/shared/util-halogen';
import {
  ChartTypes,
  DataViewResponseTypes,
  DataViewTypes,
  FlamingoTypes,
  SpaceType,
  SwitchViewResponseTypes,
} from '@mode/shared/contract-common';
import { ExplorationService, normalizeSwitchView, switchViewResponseToDataView } from '@mode/shared/data-access-webapp';
import { ModalService } from '@mode/shared/ui-angularjs';
import { generateUUID } from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsService, RunMethodType } from '@mode/shared/util-segment';
import { Switch } from '@mode/shared/util-switch';
import { ToastService } from '@mode/shared/util-toast';
import { DataViewFacade } from '@mode/switch-views/contract';
import { makeAttrsFromResources, makeRequestAttrsFromAttrFeatures } from '@mode/switch-views/domain';
import type * as ngRouter from '@uirouter/angularjs';
import * as angular from 'angular';
import { merge } from 'rxjs';
import { first, map } from 'rxjs/operators';
import explorationModalTemplate from '~/components/modal-name-exploration/name-exploration-modal.html';
import { ActiveExplorationSelectors } from '~/selectors/active-exploration.selectors';
import { ExplorerSelectors } from '~/selectors/explorer.selectors';
import { SelectedReportSelectors } from '~/selectors/selected-report.selectors';
import { AppStore } from '~/types/main.types';

export const EXPLORATION_VIEW_DATA_SERVICE_ID = 'ExplorationViewDataService';

export class ExplorationViewDataService {
  switchViewToken: string;
  newExploration: DataViewResponseTypes.Exploration;
  runViewIsRecent: boolean;
  newExplorationAnalytics: object;

  static $inject = [
    'RxStoreService',
    'ToastService',
    'ModalService',
    'AnalyticsService',
    '$state',
    '$window',
    'ExplorationService',
    'DataViewFacade',
  ];

  constructor(
    private rxStore: AppStore,
    private alertService: ToastService,
    private modalService: ModalService,
    private analyticsService: AnalyticsService,
    private $state: ngRouter.StateService,
    private $window: angular.IWindowService,
    private explorationService: ExplorationService,
    private dataViewFacade: DataViewFacade
  ) {
    // Get the current value of newExploration based on context
    merge(
      this.rxStore.select(ExplorerSelectors.getChartAsExploration),
      this.rxStore.select(ActiveExplorationSelectors.getChartAsExploration)
    ).subscribe((newExploration) => {
      this.newExploration = newExploration;
    });

    merge(
      this.rxStore.select(ExplorerSelectors.getSwitchViewToken),
      this.rxStore.select(ActiveExplorationSelectors.getSwitchViewToken)
    ).subscribe((switchViewToken) => {
      this.switchViewToken = switchViewToken;
    });

    // Get whether current run is most recent
    this.rxStore
      .select(SelectedReportSelectors.getRecentReportRunToken)
      .pipe(
        map(
          (recentReportRunToken) =>
            angular.isUndefined(this.$state.params.runId) ||
            (angular.isDefined(this.$state.params.runId) && this.$window.location.href.includes(recentReportRunToken))
        )
      )
      .subscribe((runViewIsRecent) => {
        this.runViewIsRecent = runViewIsRecent;
      });

    //analytics data for new exploration button click
    this.rxStore
      .select(ActiveExplorationSelectors.newExplorationAnalytics)
      .subscribe((newExplorationAnalytics) => (this.newExplorationAnalytics = newExplorationAnalytics));
  }

  clearExplorationResource() {
    this.rxStore.dispatch({
      type: TempActions.Type.ClearExplorationResource,
    });
  }

  /**
   * Create an encoded chart from a chart resource and a query token.
   */
  createEncodedChartFromResource(chartResource: ChartTypes.ChartResource, queryToken: string): ChartTypes.EncodedChart {
    const snapshot: ChartTypes.ChartSnapshot = {
      encoding: chartResource.view_vegas.encoding,
      format: chartResource.view_vegas.format,
      fieldFormats: chartResource.view_vegas.fieldFormats,
      fieldColors: chartResource.view_vegas.fieldColors,
      facetSizes: chartResource.view_vegas.facetSizes,
      chartType: chartResource.view_vegas.chartType,
      title: chartResource.view_vegas.title,
      description: chartResource.view_vegas.description,
      caption: chartResource.view_vegas.caption,
      colorPaletteToken: chartResource.color_palette_token,
      isAutoUpdateDisabled: chartResource.view_vegas.isAutoUpdateDisabled ?? false,
    };
    const processedResource = new Resource(chartResource) as any as ChartTypes.ChartResource;

    return {
      resource: processedResource,
      encoding: chartResource.view_vegas.encoding,
      token: chartResource.token,
      queryToken: queryToken,
      switchViewToken: chartResource.switch_view_token,
      version: chartResource.view_version,

      // TODO: need to figure out how to init history
      history: {
        undoStack: [],
        redoStack: [],
        last: snapshot,
      },
      v1View: chartResource.view,
      ...snapshot,
    };
  }

  /**
   * This method is called from the Modal component when you are doing an initial save of an exploration.
   * Prior to this getting called, explorations do not persist.
   * @param exploration Exploration
   */
  createExploration(exploration: DataViewResponseTypes.Exploration) {
    // TODO: Remove this after the `name` column on the `exploration` model in the BE is deprecated.
    // This is a simple, temporary way to ensure the new exploration name from the create modal gets
    // filled into the view_vegas blob as well.
    exploration.chart.view_vegas.title = exploration.name;

    this.dataViewFacade
      .getLocalSwitchViewAttrFeaturesBySwitchViewToken(this.switchViewToken)
      .pipe(map(makeRequestAttrsFromAttrFeatures), first())
      .subscribe((attrs) => {
        exploration.switch_view = {
          attrs,
        };

        this.explorationService
          .createExploration(this.$state.params.account, exploration)
          .then((res: any) => {
            this.$state.current.data.hasChanges = false;
            this.alertService.success('Exploration saved');
            this.$state.go('explorationShow.focus', {
              account: this.$state.params.account,
              reportId: this.$state.params.reportId,
              explorationId: res['token'],
            });
            this.trackSaveExploration(exploration, res['chart_token']);
          })
          .catch((err) => {
            this.alertService.error('There was a problem saving your exploration');
          });
      });
  }

  trackSaveExploration(exploration: DataViewResponseTypes.Exploration, chartToken: string) {
    const analyticsConfig = {
      event_id: generateUUID(),
      product_context: 'report',
      sub_context: 'exploration',
      chart_id: `chart_${chartToken}`,
      chart_type: exploration.chart.view_vegas.chartType,
    };
    this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.ExploreSaveSuccess, analyticsConfig);
  }

  trackNewExplorationClick(reportId) {
    const newExplorationsAnalyticsConfig = Object.assign(this.newExplorationAnalytics, { reportId });
    this.analyticsService.run(
      RunMethodType.Track,
      AnalyticsEvent.Name.ExploreClickExplore,
      newExplorationsAnalyticsConfig
    );
  }

  /**
   * This Modal needs to be opened in 2 contexts form the /explore route.
   * When clicking the save button in the explore header or when navigating away from the page.
   */
  openSaveExplorationModal(header: string, body: string, cancelText: string, route?: any) {
    const modalAttributes = {
      save: (exploration: DataViewResponseTypes.Exploration) => this.createExploration(exploration),
      cancelAction: () => {
        if (route) {
          route.routeData.hasChanges = false;
          this.$state.go(route.name, route.params);
        }
      },
      exploration: this.newExploration,
      isHistoricRun: !this.runViewIsRecent,
      header: header,
      body: body,
      cancelText,
    };

    const modalOptions = {
      template: explorationModalTemplate,
      windowClass: 'explore-modal',
      animation: true,
      backdrop: 'static',
    };

    this.modalService.open(modalAttributes, modalOptions);
  }

  /////////////
  // GF Methods
  /////////////

  /**
   * Get an exploration and reduce everything it needs into state
   * TODO: GF, refactor data mapping into data-access
   */
  selectExploration(accountId: string, explorationId: string, withPersistence: boolean) {
    const explorationPromise = this.explorationService.loadExploration(accountId, explorationId);

    explorationPromise.then((response) => {
      // build the FE viz
      const explorationViz = this.createEncodedChartFromResource(response._embedded.chart, response.query_token);

      // Transform dataView state
      const normalizedSwitchView: DataViewResponseTypes.NormalizedSwitchViewResponse = normalizeSwitchView(
        response._embedded.switch_view
      );

      const dataView = switchViewResponseToDataView(normalizedSwitchView);

      // TODO: Add action to charts reducer after pulling in updates to charts feature
      // Also, will clean this action up in whatever way makes the most sense once things are
      // a bit more solidified with the API and state
      this.rxStore.dispatch(
        TempActions.loadExploration({
          explorationToken: response.token,
          explorationUpdatedAt: response.updated_at,
          explorationName: explorationViz.title,
          sourceReportName: response.report_display_name,
          creator: response._embedded.creator,
          switchViewToken: response._embedded.switch_view.token,
          dataView,
          explorationViz,
          withPersistence,
          drilldownEnabled: response.report_drilldown_enabled,
          isLatestReportRun: response._embedded.report_run.is_latest_report_run,
          isLatestSuccessfulReportRun: response._embedded.report_run.is_latest_successful_report_run,
          reportHasFailuresSinceLastSuccess: response._embedded.report_run.report_has_failures_since_last_success,
          reportRunFormFields: response._embedded.report_run.form_fields,
          spaceName: response._embedded.space.name,
          spaceUrl: response._embedded.space._links.web?.href,
          isSpaceViewable: response._embedded.space['viewable?'],
          defaultAccess: response._embedded.space.default_access_level,
          isCollectionCommunity: response._embedded.space.space_type === SpaceType.community,
          isCollectionPersonal: response._embedded.space.space_type === SpaceType.private,
        })
      );
    });
  }

  /**
   * We need to build our FE SwitchView from the view resource,
   * dataset column resources, and tokens.
   */
  createGfSwitchViewForExploration(
    viewResource: SwitchViewResponseTypes.SwitchViewResource,
    columns: DataViewTypes.DatasetResourceColumn[],
    queryToken: string,
    queryRunToken: string
  ): FlamingoTypes.ModeView {
    const attrs = makeAttrsFromResources(viewResource.switch_attrs);
    return Switch.Views.make(queryToken, queryRunToken, columns, attrs, {
      weekStart: DateTime.weekStart(viewResource.week_start),
      yearStart: DateTime.yearStart(viewResource.year_start),
    });
  }
}
